"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronRight } from "lucide-react";
import TrackedLink from "@components/TrackedLink";
import { track } from "@lib/track";
import BodyBallancerBookingClient from "./BodyBallancerBookingClient";

function OverlineLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-brand)] mb-4">
      {children}
    </p>
  );
}

function Section({
  id,
  className = "",
  children,
}: {
  id?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className={`w-full px-4 sm:px-6 lg:px-8 py-16 sm:py-20 ${className}`}>
      <div className="max-w-4xl mx-auto">{children}</div>
    </section>
  );
}

function WideSection({
  id,
  className = "",
  children,
}: {
  id?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className={`w-full px-4 sm:px-6 lg:px-8 py-16 sm:py-20 ${className}`}>
      <div className="max-w-5xl mx-auto">{children}</div>
    </section>
  );
}

function BookCTA({ label, size = "lg", color = "base" }: { label: string; size?: "md" | "lg"; color?: "base" | "oat" | "duck" | "neutral" }) {
  return (
    <TrackedLink
      href="#book"
      variant="solid"
      size={size}
      color={color}
      event="pro_balancer_cta"
      label={label}
      component="BodyBallancerClient"
      className="inline-flex items-center gap-2 font-semibold"
    >
      {label}
      <ChevronRight className="h-4 w-4" />
    </TrackedLink>
  );
}

export default function BodyBallancerClient() {
  return (
    <main className="flex flex-col items-center">

      {/* ── HERO ── */}
      <section className="w-full bg-[var(--color-bg-muted)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">

          {/* Text */}
          <div>
            <hr className="border-[var(--color-brand)]/30 mb-8" />
            <OverlineLabel>Advanced Lymphatic Therapy</OverlineLabel>
            <hr className="border-[var(--color-brand)]/30 mb-10" />
            <div className="flex gap-4 sm:gap-6 mt-2">
              <div className="text-center border border-[var(--color-brand)]/20 p-5 sm:p-8 rounded-2xl flex-1">
                <div className="text-4xl sm:text-5xl font-light text-[var(--color-brand)] mb-2">25+</div>
                <div className="text-xs font-semibold uppercase tracking-widest text-gray-500">Years Clinical Experience</div>
              </div>
              <div className="text-center border border-[var(--color-brand)]/20 p-5 sm:p-8 rounded-2xl flex-1">
                <div className="text-4xl sm:text-5xl font-light text-[var(--color-brand)] mb-2">Pro</div>
                <div className="text-xs font-semibold uppercase tracking-widest text-gray-500">Athlete Background</div>
              </div>
            </div>
            <div className="mt-10 flex flex-col sm:flex-row items-start gap-4">
              <BookCTA label="Book Your First Session" />
            </div>

            {/* Special offer pill */}
            <Link
              href="/treatments/body-ballancer?offer=1#book"
              onClick={() => {
                try {
                  track(
                    "pro_balancer_offer_pill",
                    { label: "Express Mini Detox + 2 Sessions £120", component: "BodyBallancerClient" },
                    { event: "pro_balancer_offer_pill", label: "Express Mini Detox + 2 Sessions £120", component: "BodyBallancerClient" }
                  );
                } catch {}
              }}
              className="mt-6 inline-flex items-center gap-3 rounded-full border border-[var(--color-brand)]/30 bg-white pl-4 pr-5 py-2 shadow-sm hover:shadow-md transition group"
            >
              <span className="inline-block rounded-full bg-[var(--color-brand)] text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1">
                Offer
              </span>
              <span className="text-sm text-gray-700 leading-tight">
                <span className="font-semibold text-gray-900">Express Mini Detox + 2 Sessions</span>{" "}
                — <span className="font-semibold text-[var(--color-brand)]">£120</span>
                <span className="block text-xs text-gray-400">Detox pack handed to you at Stocks House</span>
              </span>
              <ChevronRight className="h-4 w-4 text-[var(--color-brand)] group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <Link
              href="/shop/bundles/express-detox-pack"
              className="mt-2 inline-block text-xs text-gray-500 underline underline-offset-4 hover:text-[var(--color-brand)]"
            >
              What&apos;s in the Express Mini Detox? →
            </Link>
          </div>

          {/* Image */}
          <div className="relative rounded-3xl overflow-hidden shadow-xl aspect-[3/4] w-full max-w-sm mx-auto lg:max-w-none">
            <Image
              src="/images/body-ballancer.webp"
              alt="Advanced lymphatic therapy — Pro Balancer treatment"
              fill
              className="object-cover object-top"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-brand)]/30 to-transparent" />
          </div>

        </div>
      </section>

      {/* ── PRACTITIONER QUOTE ── */}
      <Section className="bg-white">
        <blockquote className="border border-gray-200 rounded-2xl p-8 sm:p-10">
          <p className="text-lg sm:text-xl text-gray-600 italic leading-relaxed mb-6">
            &ldquo;With over 25 years working at the highest levels of professional sport — from international rugby and football to elite swimmers and Ironman triathletes — I have witnessed first-hand how a compromised lymphatic system silently limits recovery, performance and long-term health.&rdquo;
          </p>
          <cite className="text-xs font-semibold uppercase tracking-widest text-gray-400 not-italic">
            — Your Practitioner | 25+ Years in Elite Sport &amp; Functional Wellness
          </cite>
        </blockquote>
      </Section>

      {/* ── THE FOUNDATION ── */}
      <Section className="bg-[var(--color-bg-muted)]">
        <OverlineLabel>The Foundation</OverlineLabel>
        <h2 className="text-3xl sm:text-4xl font-light text-gray-800 leading-tight mb-8">
          Why Your Lymphatic System is the{" "}
          <em className="text-[var(--color-brand)]">Key to Everything</em>
        </h2>
        <div className="prose prose-lg max-w-none text-gray-600 space-y-4 mb-10">
          <p>
            Your lymphatic system is the body&apos;s internal plumbing.{" "}
            <strong>Unlike your blood, which has the heart as its pump, your lymph has no central pump at all.</strong>{" "}
            It depends entirely on movement, breath, muscle contraction — and when life slows you down through injury, surgery, stress, or a sedentary lifestyle, your lymph slows with it.
          </p>
          <p>
            When lymph becomes sluggish, the consequences are felt throughout the entire body: a build-up of metabolic waste, excess fluid in the tissues, sluggish detoxification, chronic low-grade inflammation, that feeling of heaviness and bloating you cannot shake, and — over time — a very real contribution to chronic disease.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="border border-[var(--color-brand)]/20 p-6 rounded-2xl text-center">
            <div className="text-3xl font-light text-[var(--color-brand)] mb-2">5</div>
            <div className="text-xs font-semibold uppercase tracking-widest text-gray-500">Key Treatment Pathways</div>
          </div>
          <div className="border border-[var(--color-brand)]/20 p-6 rounded-2xl text-center">
            <div className="text-3xl font-light text-[var(--color-brand)] mb-2">360°</div>
            <div className="text-xs font-semibold uppercase tracking-widest text-gray-500">Whole Body Approach</div>
          </div>
        </div>
      </Section>

      {/* ── THE TECHNOLOGY ── */}
      <Section className="bg-white">
        <OverlineLabel>The Technology</OverlineLabel>
        <h2 className="text-3xl sm:text-4xl font-light text-gray-800 leading-tight mb-6">
          Medical Grade.<br />
          Clinically Evidenced.<br />
          Personally Delivered.
        </h2>
        <div className="prose prose-lg max-w-none text-gray-600 space-y-4 mb-10">
          <p>
            The Pro Balancer Lymphatic System uses precision pneumatic compression therapy to externally mimic what healthy movement does naturally:{" "}
            <strong>gently and rhythmically moving lymph fluid through the body&apos;s vessels, stimulating drainage, clearing congestion, and opening the pathways your cells depend on to stay clean and functional.</strong>
          </p>
          <p>
            This is not simply a massage. This is a targeted, medically-informed protocol — designed and delivered by a practitioner with over two decades of elite sport and functional wellness experience — to transform the way your body heals, recovers, and functions at every level.
          </p>
        </div>
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-2xl overflow-hidden">
          {[
            {
              symbol: "⟁",
              heading: "Pneumatic Compression",
              body: "Precisely calibrated air pressure sequences work systematically through the lymphatic chain, replicating the rhythm of healthy manual drainage at a clinical level.",
            },
            {
              symbol: "◎",
              heading: "Myofascial Release Integration",
              body: "Combined with hands-on myofascial release techniques, each session works to release fascial restrictions that accumulate through training, posture and daily life.",
            },
            {
              symbol: "✦",
              heading: "Cellulite Reduction & Skin Smoothing",
              body: "By clearing stagnant lymph and reducing fluid congestion in subcutaneous tissue, consistent treatment visibly improves skin texture and reduces the appearance of cellulite.",
            },
            {
              symbol: "▲",
              heading: "Post-Operative Risk Reduction",
              body: "This medical-grade system is used in clinical protocols post-operatively to reduce the risk of cellulitis, lymphoedema and post-surgical fibrosis.",
            },
          ].map(({ symbol, heading, body }) => (
            <div key={heading} className="flex gap-5 p-6 bg-white">
              <div className="flex-shrink-0 w-10 h-10 border border-gray-200 rounded flex items-center justify-center text-gray-400">
                {symbol}
              </div>
              <div>
                <h3 className="font-semibold text-[var(--color-brand)] text-xs uppercase tracking-widest mb-2">{heading}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ── PRACTITIONER BACKGROUND ── */}
      <Section className="bg-[var(--color-bg-muted)]">
        <div className="prose prose-lg max-w-none text-gray-600 space-y-4">
          <p>
            Every session is built around <strong>your specific needs</strong> — whether you are recovering from surgery, managing the demands of heavy training, supporting a liver detox protocol, or simply committed to maintaining your long-term health and resilience.
          </p>
          <p>
            Having spent over 25 years working with professional athletes across rugby, football, swimming and Ironman triathlon — treating injuries, guiding rehab, and developing performance programmes at the highest level — I bring a depth of anatomical understanding, clinical insight, and functional nutrition expertise to every session.
          </p>
          <p>
            In recent years, my work has deepened into the functional medicine space, looking beyond the musculoskeletal system to address{" "}
            <strong>gut health, liver detoxification, systemic inflammation, and the gut–brain axis</strong> — because lasting health and a pain-free life depend on all of these systems working in concert.
          </p>
          <p>
            <strong>The lymphatic system sits at the crossroads of all of them.</strong> It is the single most commonly overlooked variable in both recovery and long-term wellness — and this machine gives us the ability to address it with genuine clinical precision.
          </p>
        </div>
      </Section>

      {/* ── FIVE PATHWAYS ── */}
      <WideSection className="bg-white">
        <div className="text-center mb-14">
          <OverlineLabel>What We Treat</OverlineLabel>
          <h2 className="text-3xl sm:text-4xl font-light text-gray-800 mb-4">Five Pathways to a Better Body</h2>
          <p className="text-gray-500 max-w-2xl mx-auto leading-relaxed">
            Whether your goal is faster recovery, injury prevention, improved aesthetics, surgical aftercare, or deep cellular detoxification — Pro Balancer therapy has a specific, evidence-informed role to play.
          </p>
        </div>
        <div className="space-y-8">
          {[
            {
              num: "01",
              symbol: "⟁",
              heading: "Post-Operative Recovery",
              body: "Reduce post-surgical swelling, accelerate healing, prevent cellulitis and fibrosis, and restore functional movement after joint replacements, abdominal procedures, and cosmetic surgery.",
            },
            {
              num: "02",
              symbol: "◎",
              heading: "Injury Prevention & Rehab",
              body: "Release myofascial restrictions, rebalance overused and underused muscle groups, correct postural loading patterns, and reduce the risk of injury before it happens.",
            },
            {
              num: "03",
              symbol: "✦",
              heading: "Skin & Cellulite",
              body: "Improve skin texture, reduce the appearance of cellulite, and enhance lymphatic flow through superficial tissue using targeted drainage and myofascial release techniques.",
            },
            {
              num: "04",
              symbol: "▲",
              heading: "Liver & Cellular Detox",
              body: "Open lymphatic drainage pathways to allow the liver's metabolic by-products, fat-soluble toxins, and cellular waste to be safely transported out of the body.",
            },
            {
              num: "05",
              symbol: "◇",
              heading: "Athletic Recovery & Longevity",
              body: "Reduce DOMS, accelerate training recovery, lower systemic inflammation, and build a sustainable foundation for peak performance and pain-free living at every age.",
            },
          ].map(({ num, symbol, heading, body }) => (
            <div key={num} className="flex gap-6 items-start border-b border-gray-100 pb-8 last:border-0 last:pb-0">
              <div className="flex-shrink-0 w-10 h-10 border border-gray-200 rounded flex items-center justify-center text-gray-400">
                {symbol}
              </div>
              <div className="flex-1">
                <div className="flex items-baseline gap-4 mb-2">
                  <h3 className="text-lg font-light text-gray-800">{heading}</h3>
                  <span className="text-3xl font-light text-[var(--color-brand)]/20 ml-auto">{num}</span>
                </div>
                <p className="text-gray-600 leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-8 p-8 border border-gray-100 rounded-2xl text-gray-400">
          <div className="flex gap-4 items-start">
            <span className="text-2xl flex-shrink-0">⬡</span>
            <div>
              <h3 className="font-light text-xl text-gray-500 mb-2">Whole Body Wellbeing</h3>
              <p className="text-gray-400 leading-relaxed">
                When your lymphatic system flows freely, everything changes — energy, mood, clarity, resilience. This is the foundation of lasting health, not a luxury add-on.
              </p>
            </div>
          </div>
        </div>
      </WideSection>

      {/* ── SURGICAL RECOVERY ── */}
      <Section id="surgical-recovery" className="bg-[var(--color-bg-muted)]">
        <OverlineLabel>Surgical Recovery</OverlineLabel>
        <h2 className="text-3xl sm:text-4xl font-light text-gray-800 leading-tight mb-8">
          What Every Patient Should Know Before — and After — Their Operation
        </h2>
        <div className="prose prose-lg max-w-none text-gray-600 space-y-4 mb-12">
          <p>
            Post-operative lymphatic drainage is no longer a nice-to-have. It is fast becoming a clinical standard of care — and with good reason. Surgery, however routine, creates trauma to the surrounding tissue. That trauma triggers an inflammatory cascade. The lymphatic system, responsible for clearing that excess fluid and metabolic debris, is often overwhelmed at exactly the time the patient is least able to move.
          </p>
        </div>

        {/* Protocol */}
        <div className="border-l-2 border-[var(--color-brand)]/30 pl-8 mb-12">
          <h3 className="text-xl font-light text-gray-700 mb-4">The Clinical Protocol: When to Start &amp; How Often</h3>
          <p className="text-sm text-gray-500 mb-6 leading-relaxed">
            Based on published post-operative lymphatic drainage guidelines, the following framework applies — always in consultation with your surgical team:
          </p>
          <div className="space-y-4">
            {[
              {
                phase: "Phase One",
                timing: "Days 5–7",
                body: "Earliest point to begin, once the surgical site is stable. Gentle, broad lymphatic stimulation begins to move post-surgical fluid away from the area.",
              },
              {
                phase: "Phase Two",
                timing: "2–3× per week",
                body: "Weeks 1–4 post-surgery. The most critical window. Regular sessions at this frequency significantly reduce swelling, lower risk of fibrosis, and accelerate the return of functional range of motion.",
              },
              {
                phase: "Phase Three",
                timing: "1× per week",
                body: "Weeks 4–8 and beyond. Maintenance and consolidation. As healing progresses, weekly sessions sustain lymphatic flow, support scar tissue management (from week 6 when skin is fully closed), and continue to support the immune response.",
              },
            ].map(({ phase, timing, body }) => (
              <div key={phase} className="border border-gray-200 rounded-2xl p-6 bg-white text-center">
                <div className="text-xs font-semibold uppercase tracking-widest text-[var(--color-brand)] mb-2">{phase}</div>
                <div className="text-2xl font-light text-gray-700 mb-3">{timing}</div>
                <p className="text-sm text-gray-500 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-gray-400 italic leading-relaxed">
            &dagger; Always follow your surgeon&apos;s specific post-operative guidance. Scar tissue work begins only once the wound is fully closed and shows no signs of infection, typically 6–8 weeks post-surgery.
          </p>
        </div>

        {/* Sedentary recovery */}
        <div className="mb-12">
          <h3 className="text-2xl font-light text-gray-800 mb-4">Why Sedentary Recovery Makes This More Urgent</h3>
          <p className="text-gray-600 leading-relaxed mb-4">
            When we are forced to rest following surgery, the lymphatic system — which depends entirely on movement and muscle contraction to propel fluid — slows dramatically. This is not merely uncomfortable. A sluggish lymphatic system during recovery means slower fluid clearance, more persistent swelling, a heavier inflammatory burden, impaired immune response, and an increased risk of post-operative complications including cellulitis.
          </p>
          <p className="text-gray-600 leading-relaxed">
            The result is often that patients feel heavier, more fatigued, more demotivated, and less equipped to do the rehabilitation work that determines their long-term outcome.{" "}
            <strong>Pro Balancer therapy directly addresses this by doing what the body cannot do for itself during forced rest.</strong>
          </p>
        </div>

        {/* Procedure cards */}
        <div className="space-y-6">
          {[
            {
              heading: "Knee & Hip Replacement",
              tag: "Joint Replacement",
              body: "Joint replacement surgery disrupts the lymphatic network around the operative site, creating significant post-surgical oedema that directly limits the range of movement needed for effective physiotherapy. Lymphatic drainage has been shown to reduce this swelling from the first session, improving comfort, accelerating the return of mobility, and allowing patients to engage more productively with their rehabilitation programme.",
              testimonial: '"From the first session, the swelling visibly reduced and moving became easier. By the end of the programme, my recovery was ahead of everything the surgical team had predicted." — Total Knee Replacement Patient',
            },
            {
              heading: "Endometriosis & Abdominal Surgery",
              tag: "Abdominal Recovery",
              body: "Abdominal surgery — whether for endometriosis, gynaecological conditions, or digestive pathology — creates significant disruption to the lymphatic vessels of the pelvis and abdomen. Post-surgical adhesions, bloating, and persistent oedema are common. Targeted lymphatic drainage not only accelerates fluid clearance but helps prevent the formation of fibrotic adhesion tissue that can be the source of long-term discomfort and complication.",
              testimonial: '"The bloating and heaviness after my laparoscopy was debilitating. After three sessions, I felt like myself again — lighter, less inflamed, and finally able to move without pain." — Endometriosis Patient',
            },
            {
              heading: "Postpartum Recovery",
              tag: "Post-Birth",
              body: "Whether following vaginal birth or caesarean section, the postpartum body carries an extraordinary fluid load alongside the demands of sleep deprivation, hormonal fluctuation, and the physical toll of feeding and caring for a newborn. Lymphatic drainage helps the body clear retained fluid rapidly, supports the healing of abdominal and perineal tissue, and — at a time when motivation and energy are already stretched thin — provides a profound sense of physical relief and restoration that supports emotional wellbeing too.",
              testimonial: "\"Within one session I could see the difference in my abdomen and I felt so much lighter. I only wish I'd known about this sooner.\" — Postpartum Client",
            },
            {
              heading: "Liposuction & Cosmetic Surgery",
              tag: "Aesthetic Recovery",
              body: "Liposuction and cosmetic procedures carry a particularly high lymphatic demand: the disruption to subcutaneous lymphatic vessels means fluid redistribution is significant and, without active intervention, fibrotic nodules and uneven skin texture are common. Post-operative lymphatic drainage is widely recommended by plastic surgeons as an essential component of the recovery protocol — reducing contour irregularities, minimising bruising, preventing fibrosis, and dramatically shortening recovery timelines.",
              testimonial: '"My plastic surgeon told me the quality of my result would depend on my post-op care. This treatment made the difference between a good outcome and an exceptional one." — Liposuction Patient',
            },
          ].map(({ heading, tag, body, testimonial }) => (
            <div key={heading} className="rounded-2xl overflow-hidden border border-gray-200 bg-white border-l-4 border-l-[var(--color-brand)]/40">
              <div className="p-6 sm:p-8">
                <h3 className="text-lg font-light text-gray-800 mb-1">{heading}</h3>
                <div className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">{tag}</div>
                <p className="text-gray-600 leading-relaxed mb-5">{body}</p>
                <hr className="border-gray-100 mb-5" />
                <p className="text-sm text-gray-500 italic leading-relaxed">{testimonial}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ── INJURY PREVENTION ── */}
      <Section id="injury-prevention" className="bg-white">
        <OverlineLabel>Injury Prevention &amp; Performance</OverlineLabel>
        <h2 className="text-3xl sm:text-4xl font-light text-gray-800 leading-tight mb-8">
          Most Injuries Don&apos;t Happen by Accident. They Build Over Time.
        </h2>
        <div className="prose prose-lg max-w-none text-gray-600 space-y-4 mb-10">
          <p>
            Unless you have been in a collision — a car accident, a rugby tackle, a sudden traumatic event — the vast majority of injuries are not random. They are the predictable result of imbalances that accumulate quietly, over months and years, until the point of failure.
          </p>
          <p>
            Your body is highly adaptive.{" "}
            <strong>It will mould itself to the positions and demands you place on it — and when those demands are uneven, imbalanced, or repetitive, the consequences are structural.</strong>
          </p>
          <p>
            Prolonged sitting tightens the hip flexors and quadriceps whilst the glutes — arguably the most important force-generating muscle group in the body — progressively disengage. Tight hip flexors and inactive glutes are one of the most well-evidenced contributors to lower back pain and hip pathology, and yet they are so common that most people accept them as an inevitable part of modern life.
          </p>
          <p>
            <strong>Regular fascial and lymphatic work is not simply recovery. It is maintenance of the human machine.</strong> Think of it the same way you think about servicing a car: consistent, proactive attention prevents the breakdown that costs far more — in time, pain, and lost training — than the maintenance ever would.
          </p>
        </div>

        {/* DOMS callout */}
        <div className="border border-[var(--color-brand)]/20 rounded-2xl p-6 sm:p-8 mb-10 bg-[var(--color-bg-muted)]">
          <h3 className="text-xl font-light text-gray-800 mb-4">Stack Your Recovery: Reduce DOMS Intelligently</h3>
          <p className="text-gray-600 leading-relaxed">
            Delayed Onset Muscle Soreness (DOMS) is the result of micro-trauma and the subsequent inflammatory response in muscle tissue. Lymphatic drainage directly supports the clearance of these inflammatory by-products from the tissue, reducing the intensity and duration of soreness.{" "}
            <strong>Schedule your session following the hardest training session of your week</strong> — not only will you recover faster, but the session can be adapted to specifically target the muscle groups under the greatest load, giving you a personalised recovery protocol that compounds your training benefit.
          </p>
        </div>

        {/* Common patterns */}
        <OverlineLabel>Common Patterns We Address</OverlineLabel>
        <div className="space-y-4">
          {[
            { label: "Desk worker hip pattern", body: "Shortened hip flexors, inhibited glutes, anterior pelvic tilt — the foundation of most chronic lower back pain." },
            { label: "Runner's fascial loading", body: "ITB tightness, plantar fascia restriction, and calf/Achilles overload from repetitive unilateral loading patterns." },
            { label: "Cyclist's anterior chain dominance", body: "Compressed hip flexors, thoracic kyphosis, neck tension — creating shoulder and knee vulnerability." },
            { label: "Swimmer's shoulder", body: "Overloaded rotator cuff and lat dominance creating internal rotation restriction and impingement risk." },
            { label: "Team sport asymmetry", body: "Dominant leg loading patterns, adductor/abductor imbalance, and repetitive rotational forces through the lumbar spine." },
            { label: "Post-surgical compensation", body: "The entire body adapts its movement patterns around pain — creating secondary imbalances that outlast the original injury." },
          ].map(({ label, body }) => (
            <div key={label} className="border-b border-gray-100 pb-4 last:border-0">
              <span className="font-semibold text-[var(--color-brand)]">{label}: </span>
              <span className="text-gray-600">{body}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* ── LIVER-LYMPH CONNECTION ── */}
      <Section id="liver-lymph" className="bg-[var(--color-bg-muted)]">
        <OverlineLabel>The Liver–Lymph Connection</OverlineLabel>
        <h2 className="text-3xl sm:text-4xl font-light text-gray-800 leading-tight mb-8">
          Your Liver is Your <em className="text-[var(--color-brand)]">Life Force</em>.<br />
          Your Lymph is Its Lifeline.
        </h2>
        <div className="prose prose-lg max-w-none text-gray-600 space-y-4 mb-10">
          <p>
            The liver is the body&apos;s master detoxification organ — processing environmental toxins, metabolic by-products, used hormones, pharmaceutical residues, food additives, and the constant stream of cellular waste that every living body generates simply by being alive. In the modern world, that burden has never been greater.
          </p>
          <p>
            But here is what most detox protocols miss entirely:{" "}
            <strong>the liver cannot complete its work alone.</strong> Once the liver has processed toxins through its two-phase enzymatic pathway, those by-products must be transported out of the body. That transport system is the lymphatic network.
          </p>
          <p>
            Critically,{" "}
            <strong>fat-soluble toxins, metabolic by-products, and excess fats are moved exclusively through the lymphatic system</strong>{" "}
            — not the bloodstream. If the lymph is sluggish, those processed toxins have nowhere to go. They recirculate. They redeposit in tissue. They contribute to the very toxic load the detox protocol was designed to reduce.
          </p>
          <p>
            <strong>Any serious liver detoxification protocol that does not explicitly address lymphatic flow is incomplete.</strong> Lymphatic drainage is not an optional extra to a liver cleanse — it is the drainage pipe without which the drain cannot function.
          </p>
        </div>

        {/* Open Drain Principle */}
        <div className="border-l-2 border-[var(--color-brand)]/30 pl-8 mb-10 py-2">
          <h3 className="text-xl font-light text-gray-700 mb-3">The Open Drain Principle</h3>
          <p className="text-gray-500 leading-relaxed">
            Think of your body&apos;s detox system as a series of drains, each feeding into the next. The lymphatic system must be open and flowing before the liver can effectively offload its processed waste. A blocked lymph is the equivalent of a sink with no drain — no matter how powerful the tap, nothing moves. Every liver detox, cellular cleanse, or anti-inflammatory protocol becomes dramatically more effective when the lymphatic drainage pathway is actively supported.
          </p>
        </div>

        {/* Three phases */}
        <OverlineLabel>Liver Detox: The Three Phases</OverlineLabel>
        <div className="space-y-3 mb-10">
          {[
            {
              phase: "Phase 1",
              heading: "Transformation",
              body: "The liver's cytochrome P450 enzyme system converts fat-soluble toxins into intermediate metabolites. These intermediates are often more reactive than the original toxin and must be rapidly processed in Phase 2.",
            },
            {
              phase: "Phase 2",
              heading: "Conjugation",
              body: "Intermediate metabolites are bound to water-soluble compounds (glutathione, sulphate, glycine) making them safe for excretion. This phase is highly nutrient-dependent and easily overwhelmed by modern toxic load.",
            },
            {
              phase: "Phase 3",
              heading: "Transport & Elimination",
              body: "The completed water-soluble conjugates must now leave the body — via the gut, kidneys, and critically, the lymphatic system. This is the phase where Pro Balancer therapy has its most direct impact.",
            },
          ].map(({ phase, heading, body }) => (
            <div key={phase} className="flex gap-5 p-5 border border-gray-200 rounded-xl bg-white">
              <div className="flex-shrink-0 text-xs font-semibold uppercase tracking-widest text-gray-400 w-14 pt-0.5">{phase}</div>
              <div>
                <h4 className="font-semibold text-gray-700 mb-1">{heading}</h4>
                <p className="text-sm text-gray-500 leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Signs sluggish */}
        <div className="border border-gray-200 rounded-2xl p-6 bg-white">
          <OverlineLabel>Signs Your Lymph Is Sluggish</OverlineLabel>
          <div className="grid grid-cols-2 gap-0">
            {[
              "Persistent fatigue", "Chronic bloating",
              "Puffy skin & fluid retention", "Brain fog",
              "Recurrent illness", "Low motivation",
              "Skin breakouts", "Cellulite build-up",
            ].map((sign) => (
              <div key={sign} className="text-sm text-gray-600 py-3 border-b border-gray-100 last:border-0 pr-4">
                {sign}
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── AESTHETICS & SKIN HEALTH ── */}
      <Section id="skin-health" className="bg-white">
        <OverlineLabel>Aesthetics &amp; Skin Health</OverlineLabel>
        <h2 className="text-3xl sm:text-4xl font-light text-gray-800 leading-tight mb-8">
          Cellulite Is a Lymphatic Problem. Here&apos;s How We Solve It.
        </h2>
        <div className="prose prose-lg max-w-none text-gray-600 space-y-4 mb-10">
          <p>
            Cellulite is not simply a fat storage issue — it is the result of impaired lymphatic drainage and compromised circulation in the subcutaneous tissue. When lymph pools in the connective tissue beneath the skin, it creates the characteristic dimpled appearance associated with cellulite. The fascia — the connective tissue network that envelopes every muscle, organ, and structure in the body — becomes thickened and fibrous where drainage is poor.
          </p>
          <p>
            Our protocol combines the Pro Balancer&apos;s precision pneumatic drainage with targeted myofascial release to directly address both the fluid congestion and the fascial restriction. The result is not a temporary fix — it is a genuine improvement in tissue quality, circulation, and skin texture that develops progressively with consistent treatment.
          </p>
          <p>
            Smoother, firmer, healthier-looking skin is not a cosmetic illusion. It is the visible evidence of a lymphatic system that is doing its job — and tissue that is being properly nourished, drained, and maintained.
          </p>
        </div>
        <div className="border-l-2 border-[var(--color-brand)]/30 pl-8 space-y-8">
          {[
            {
              heading: "Myofascial Release",
              body: "Fascia that has become thickened and restricted through poor drainage, repetitive stress, or inactivity directly contributes to cellulite formation. Myofascial release techniques restore the fascia's natural suppleness, allowing fluid to move freely through the tissue.",
            },
            {
              heading: "Subcutaneous Drainage",
              body: "Pro Balancer's calibrated compression sequences work at the level of the superficial lymphatic network — the fine vessels that drain the skin and subcutaneous fat layer — clearing the stagnant fluid that creates the dimpling effect.",
            },
            {
              heading: "Collagen & Tissue Nutrition",
              body: "When lymphatic flow is restored, so is the effective delivery of nutrients to the skin cells and the efficient removal of waste products. This supports the healthy collagen environment that gives skin its firmness, elasticity, and glow.",
            },
            {
              heading: "Progressive Improvement",
              body: "Results are cumulative. A course of sessions produces visible, lasting improvement in skin texture — not the temporary puffiness of water retention manipulation, but a genuine structural improvement in tissue quality.",
            },
          ].map(({ heading, body }) => (
            <div key={heading}>
              <h3 className="font-semibold text-xs uppercase tracking-widest text-[var(--color-brand)] mb-2">{heading}</h3>
              <p className="text-gray-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ── THE OUTCOMES ── */}
      <Section id="outcomes" className="bg-[var(--color-bg-muted)]">
        <div className="text-center mb-12">
          <OverlineLabel>The Outcomes</OverlineLabel>
          <h2 className="text-3xl sm:text-4xl font-light text-gray-800">What Changes When Your Lymph Flows Freely</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
          <div>
            <h3 className="text-xl font-light text-gray-500 mb-6 italic">You&apos;ll Feel It Quickly</h3>
            <ul className="space-y-4">
              {[
                "A noticeable increase in energy and mental clarity — as metabolic waste clears from the tissues",
                "Reduced bloating and that persistent feeling of heaviness in the legs and abdomen",
                "Faster recovery from training sessions, with significantly reduced DOMS",
                "Visible reduction in post-surgical or post-injury swelling, often from the first session",
                "Improved skin texture and progressive reduction in the appearance of cellulite",
                "Released myofascial tension, improved posture and freedom of movement",
                "A profound sense of physical lightness and wellbeing following each session",
              ].map((item) => (
                <li key={item} className="text-sm text-gray-600 leading-relaxed">
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-xl font-light text-gray-500 mb-6 italic">The Long Game</h3>
            <ul className="space-y-4">
              {[
                "Healthy, open detox pathways that reduce the risk of chronic inflammatory conditions driven by toxic accumulation",
                "Lower systemic inflammation — one of the primary drivers of chronic disease, accelerated ageing, and pain",
                "A structurally balanced musculoskeletal system that is more resilient to injury and more responsive to training",
                "Sustained liver function and efficient cellular detoxification — the foundation of genuine longevity",
                "Reduced oxidative stress at the cellular level — supporting brain health, immune function, and metabolic efficiency",
                "Maintained fascial health and joint integrity, reducing the degenerative changes that accumulate through years of unaddressed restriction",
                "A body that ages well — moving freely, recovering efficiently, and remaining vital well into later life",
              ].map((item) => (
                <li key={item} className="text-sm text-gray-600 leading-relaxed">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* ── IMAGE BREAK ── */}
      <div className="w-full relative overflow-hidden" style={{ height: "40vh", minHeight: "240px", maxHeight: "480px" }}>
        <Image
          src="/images/body-ballancer.webp"
          alt="Pro Balancer lymphatic therapy session"
          fill
          className="object-cover object-center"
        />
        <div className="absolute inset-0 bg-[var(--color-brand)]/20" />
      </div>

      {/* ── THE BIGGER PICTURE ── */}
      <Section id="bigger-picture" className="bg-white">
        <OverlineLabel>The Bigger Picture</OverlineLabel>
        <h2 className="text-3xl sm:text-4xl font-light text-gray-800 leading-tight mb-8">
          Lymphatic Health <em className="text-[var(--color-brand)]">Is</em> Longevity
        </h2>
        <div className="prose prose-lg max-w-none text-gray-600 space-y-4 mb-8">
          <p>
            We live in a world that places an ever-growing burden on our bodies. Environmental toxins, ultra-processed foods, psychological stress, electromagnetic exposure, pharmaceutical residues, and the relentless pace of modern life create a toxic load that our ancestors simply never faced. We cannot eliminate these external pressures. We can, however, become far more deliberate about how effectively we remove them from our system.
          </p>
        </div>
        <blockquote className="border border-gray-200 rounded-2xl p-8 mb-8">
          <p className="text-lg text-gray-500 italic leading-relaxed text-center">
            &ldquo;The question is not whether your body is being exposed to toxins and inflammatory burden — in the modern world, it inevitably is. The question is how efficiently your vessel is equipped to move them out.&rdquo;
          </p>
        </blockquote>
        <div className="prose prose-lg max-w-none text-gray-600 space-y-4">
          <p>
            In functional medicine, we talk about the concept of the drainage funnel: colon first, then gut, then lymph, then liver — each layer must be open before the one above can offload effectively. Chronic fatigue, persistent inflammation, hormonal imbalance, skin conditions, joint pain, brain fog — these are often not isolated symptoms. They are signals from a system that is backed up, overwhelmed, and struggling to keep the drainage flowing.
          </p>
          <p>
            Regular Pro Balancer lymphatic drainage therapy is one of the most direct, evidence-supported interventions available to keep that system open — and to give your body the best possible conditions to heal, regenerate, and thrive.
          </p>
        </div>
      </Section>

      {/* ── BRAIN CONNECTION ── */}
      <Section id="brain-connection" className="bg-[var(--color-bg-muted)]">
        <OverlineLabel>The Brain Connection</OverlineLabel>
        <h2 className="text-3xl sm:text-4xl font-light text-gray-800 leading-tight mb-8">
          What You Do During the Day Determines How Well Your Brain{" "}
          <em className="text-[var(--color-brand)]">Detoxes at Night</em>
        </h2>
        <div className="prose prose-lg max-w-none text-gray-600 space-y-4 mb-10">
          <p>
            For decades, scientists assumed the brain had no lymphatic system of its own. We now know differently.{" "}
            <strong>The glymphatic system — named as a fusion of &ldquo;glial cells&rdquo; and &ldquo;lymphatic&rdquo; — is the brain&apos;s own dedicated waste clearance network,</strong>{" "}
            discovered only in 2012 and now the subject of intense research worldwide.
          </p>
          <p>
            It works like this: whilst you sleep, cerebrospinal fluid (CSF) is pumped through channels in the brain tissue, flushing out the metabolic by-products, inflammatory proteins, and toxic debris that have accumulated during your waking hours.{" "}
            <strong>During deep sleep, the brain&apos;s interstitial space actually expands by up to 60%</strong>{" "}
            — creating the room needed for this remarkable nightly cleanse to take place.
          </p>
          <p>
            The glymphatic system is responsible for removing amyloid-beta and tau proteins — the very compounds that accumulate in Alzheimer&apos;s disease — as well as reactive oxygen species and pro-inflammatory cytokines.{" "}
            <strong>A well-functioning glymphatic system is, quite literally, one of the most powerful tools we have against neurodegeneration.</strong>
          </p>
        </div>

        {/* Day/Night */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
          <div className="text-center p-8 border border-gray-200 rounded-2xl bg-white">
            <div className="text-3xl mb-3">☀</div>
            <div className="text-xs font-semibold uppercase tracking-widest text-[var(--color-brand)] mb-3">Daytime — Your Role</div>
            <p className="text-sm text-gray-500 leading-relaxed">
              Pro Balancer therapy stimulates peripheral lymphatic flow, opening the cervical and meningeal lymphatic vessels that serve as the brain&apos;s drainage exit point.
            </p>
          </div>
          <div className="text-center p-8 border border-gray-200 rounded-2xl bg-white">
            <div className="text-3xl mb-3">☽</div>
            <div className="text-xs font-semibold uppercase tracking-widest text-[var(--color-brand)] mb-3">Night-time — The Brain&apos;s Role</div>
            <p className="text-sm text-gray-500 leading-relaxed">
              With drainage pathways open, the glymphatic system can perform its nightly cleanse more efficiently — clearing amyloid-beta, tau, and oxidative debris from the brain tissue.
            </p>
          </div>
        </div>

        <blockquote className="border border-gray-200 rounded-2xl p-6 sm:p-8 bg-white mb-10 text-center">
          <p className="text-gray-500 italic leading-relaxed">
            A body whose lymphatic system flows freely by day gives the brain the conditions it needs to detox deeply by night. These two systems are not separate — they are a continuum.
          </p>
        </blockquote>

        <div className="border-l-2 border-[var(--color-brand)]/30 pl-8 space-y-8 mb-10">
          {[
            {
              heading: "Amyloid & Tau Clearance",
              body: "The glymphatic system is the primary pathway for clearing amyloid-beta and tau proteins — the toxic accumulations associated with Alzheimer's and Parkinson's disease. Impaired glymphatic function means these proteins accumulate faster than they are cleared, accelerating neurodegeneration.",
            },
            {
              heading: "Oxidative Stress Reduction",
              body: "Every waking hour, the brain generates reactive oxygen species as a by-product of its extraordinary energy demands. The glymphatic system removes these free radicals during sleep. When this clearance is impaired, oxidative stress builds within the nervous system — contributing to brain fog, mood dysregulation, and accelerated cognitive decline.",
            },
            {
              heading: "Sleep Quality & The Feedback Loop",
              body: "The relationship between glymphatic function and sleep is bidirectional: poor glymphatic drainage impairs sleep quality, and poor sleep impairs glymphatic drainage. Supporting peripheral lymphatic flow helps break this cycle — creating the conditions for deeper, more restorative sleep and more effective nightly brain detoxification.",
            },
            {
              heading: "The Cervical Lymph Connection",
              body: "Cerebrospinal fluid and glymphatic waste drain directly into the cervical (neck) lymphatic vessels before entering the broader lymphatic system. This is why Pro Balancer therapy — which works across the whole lymphatic chain including the cervical nodes — has a direct and meaningful influence on the brain's ability to cleanse itself each night.",
            },
          ].map(({ heading, body }) => (
            <div key={heading}>
              <h3 className="font-semibold text-xs uppercase tracking-widest text-[var(--color-brand)] mb-2">{heading}</h3>
              <p className="text-gray-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>

        <div className="border border-[var(--color-brand)]/20 rounded-2xl p-8 text-center">
          <h3 className="text-xl font-light text-gray-700 mb-4">Detoxification — and the Lymphatic System Is Its Gateway</h3>
          <p className="text-sm text-gray-500 leading-relaxed">
            We invest in our bodies. We exercise, we eat well, we manage stress. But if the glymphatic system is not supported — if the overnight brain cleanse is compromised by poor peripheral lymphatic flow, poor sleep, or an overwhelmed drainage pathway — then the oxidative load within the brain quietly accumulates, year after year.{" "}
            <strong>Supporting your lymphatic system is not just a body investment. It is the most direct, evidence-supported thing you can do to protect your brain health for the long term.</strong>
          </p>
        </div>
      </Section>

      {/* ── BOOK ── */}
      <Section id="book" className="bg-[var(--color-bg-muted)]">
        <div className="mb-10">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-brand)] mb-4">Book Online</p>
          <h2 className="text-3xl sm:text-4xl font-light text-gray-800 leading-tight mb-4">
            Your Health Cannot Wait.<br />Neither Should You.
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl leading-relaxed">
            Whether you are preparing for surgery, recovering from one, managing the demands of training, or simply committed to understanding and supporting your body at a deeper level — there is a session designed for you.
          </p>
        </div>
        <BodyBallancerBookingClient />
        <p className="mt-6 text-xs text-gray-400 leading-relaxed">
          The Pro Balancer Lymphatic System is a medical-grade therapeutic device. All treatments are delivered by a qualified practitioner with 25+ years of clinical experience. Post-operative treatments are offered in conjunction with, and with the approval of, your surgical team. Individual results may vary. This page is for informational purposes and does not constitute medical advice. Always consult your healthcare provider before commencing any new therapeutic protocol following surgery or medical treatment.
        </p>
      </Section>

    </main>
  );
}
